import * as tf from '@tensorflow/tfjs';

export class AIEmergencyEngine {
  private model: tf.LayersModel | null = null;
  private isInitialized = false;

  constructor() {
    this.initializeModel();
  }

  private async initializeModel() {
    try {
      // In a real implementation, you would load a pre-trained model
      // For demo purposes, we'll create a simple model structure
      this.model = tf.sequential({
        layers: [
          tf.layers.dense({ inputShape: [100], units: 64, activation: 'relu' }),
          tf.layers.dropout({ rate: 0.2 }),
          tf.layers.dense({ units: 32, activation: 'relu' }),
          tf.layers.dense({ units: 4, activation: 'softmax' }) // 4 severity levels
        ]
      });

      this.isInitialized = true;
      console.log('AI Emergency Engine initialized');
    } catch (error) {
      console.error('Failed to initialize AI model:', error);
    }
  }

  async classifyEmergency(description: string): Promise<{
    type: string;
    severity: string;
    confidence: number;
    keywords: string[];
    riskFactors: string[];
    recommendedActions: string[];
  }> {
    // Simulate AI processing delay
    await new Promise(resolve => setTimeout(resolve, 1000));

    const keywords = this.extractKeywords(description);
    const riskFactors = this.identifyRiskFactors(description, keywords);
    const { type, severity, confidence } = this.classifyBySeverity(description, keywords);
    const recommendedActions = this.generateRecommendations(type, severity, riskFactors);

    return {
      type,
      severity,
      confidence,
      keywords,
      riskFactors,
      recommendedActions
    };
  }

  private extractKeywords(description: string): string[] {
    const emergencyKeywords = {
      fire: ['fire', 'smoke', 'burning', 'flames', 'explosion', 'gas leak'],
      medical: ['heart', 'breathing', 'unconscious', 'bleeding', 'pain', 'injury', 'overdose'],
      police: ['robbery', 'assault', 'theft', 'violence', 'weapon', 'domestic'],
      rescue: ['trapped', 'stuck', 'drowning', 'fall', 'collapsed', 'missing'],
      accident: ['crash', 'collision', 'vehicle', 'motorcycle', 'pedestrian', 'hit']
    };

    const foundKeywords: string[] = [];
    const lowerDescription = description.toLowerCase();

    Object.values(emergencyKeywords).flat().forEach(keyword => {
      if (lowerDescription.includes(keyword)) {
        foundKeywords.push(keyword);
      }
    });

    return foundKeywords;
  }

  private identifyRiskFactors(description: string, keywords: string[]): string[] {
    const riskFactors: string[] = [];
    const lowerDescription = description.toLowerCase();

    const riskIndicators = {
      'multiple casualties': ['multiple', 'several', 'many people'],
      'hazardous materials': ['chemical', 'toxic', 'hazmat', 'gas'],
      'structural damage': ['building', 'collapse', 'structure'],
      'weather related': ['storm', 'flood', 'wind', 'ice'],
      'traffic hazard': ['highway', 'intersection', 'traffic'],
      'pediatric emergency': ['child', 'baby', 'infant', 'kid'],
      'elderly emergency': ['elderly', 'senior', 'nursing home'],
      'mental health crisis': ['suicide', 'mental', 'psychiatric']
    };

    Object.entries(riskIndicators).forEach(([risk, indicators]) => {
      if (indicators.some(indicator => lowerDescription.includes(indicator))) {
        riskFactors.push(risk);
      }
    });

    return riskFactors;
  }

  private classifyBySeverity(description: string, keywords: string[]): {
    type: string;
    severity: string;
    confidence: number;
  } {
    const lowerDescription = description.toLowerCase();
    
    // Critical indicators
    const criticalIndicators = [
      'not breathing', 'unconscious', 'cardiac arrest', 'massive bleeding',
      'explosion', 'building collapse', 'multiple casualties', 'active shooter'
    ];

    // High severity indicators
    const highIndicators = [
      'severe', 'major', 'serious', 'life threatening', 'emergency',
      'urgent', 'critical', 'immediate'
    ];

    // Determine emergency type
    let type = 'other';
    let maxKeywordCount = 0;

    const typeKeywords = {
      fire: ['fire', 'smoke', 'burning', 'flames'],
      medical: ['medical', 'heart', 'breathing', 'injury', 'pain'],
      police: ['police', 'crime', 'violence', 'theft'],
      rescue: ['rescue', 'trapped', 'stuck', 'drowning'],
      accident: ['accident', 'crash', 'collision', 'vehicle']
    };

    Object.entries(typeKeywords).forEach(([emergencyType, typeWords]) => {
      const matchCount = typeWords.filter(word => lowerDescription.includes(word)).length;
      if (matchCount > maxKeywordCount) {
        maxKeywordCount = matchCount;
        type = emergencyType;
      }
    });

    // Determine severity
    let severity = 'medium';
    let confidence = 0.7;

    if (criticalIndicators.some(indicator => lowerDescription.includes(indicator))) {
      severity = 'critical';
      confidence = 0.95;
    } else if (highIndicators.some(indicator => lowerDescription.includes(indicator))) {
      severity = 'high';
      confidence = 0.85;
    } else if (lowerDescription.includes('minor') || lowerDescription.includes('small')) {
      severity = 'low';
      confidence = 0.8;
    }

    return { type, severity, confidence };
  }

  private generateRecommendations(type: string, severity: string, riskFactors: string[]): string[] {
    const recommendations: string[] = [];

    // Base recommendations by type
    const typeRecommendations = {
      fire: ['Dispatch fire department', 'Evacuate area', 'Check for gas leaks'],
      medical: ['Dispatch paramedics', 'Provide first aid instructions', 'Clear access route'],
      police: ['Dispatch police units', 'Secure perimeter', 'Protect witnesses'],
      rescue: ['Dispatch rescue team', 'Assess structural safety', 'Prepare specialized equipment'],
      accident: ['Dispatch emergency services', 'Control traffic', 'Check for injuries']
    };

    recommendations.push(...(typeRecommendations[type as keyof typeof typeRecommendations] || []));

    // Severity-based recommendations
    if (severity === 'critical') {
      recommendations.push('Priority 1 response', 'Multiple unit dispatch', 'Hospital notification');
    } else if (severity === 'high') {
      recommendations.push('Expedited response', 'Supervisor notification');
    }

    // Risk factor recommendations
    riskFactors.forEach(risk => {
      switch (risk) {
        case 'multiple casualties':
          recommendations.push('Mass casualty protocol', 'Additional ambulances');
          break;
        case 'hazardous materials':
          recommendations.push('Hazmat team dispatch', 'Evacuation zone');
          break;
        case 'pediatric emergency':
          recommendations.push('Pediatric specialist', 'Child services notification');
          break;
      }
    });

    return [...new Set(recommendations)]; // Remove duplicates
  }

  async predictOptimalRoute(
    startLocation: { latitude: number; longitude: number },
    endLocation: { latitude: number; longitude: number },
    responderSkills: string[],
    currentTraffic: string = 'moderate'
  ): Promise<{
    estimatedTime: number;
    trafficImpact: number;
    skillMatchBonus: number;
    optimalRoute: Array<{ latitude: number; longitude: number }>;
  }> {
    // Simulate AI route optimization
    await new Promise(resolve => setTimeout(resolve, 500));

    const distance = this.calculateDistance(
      startLocation.latitude,
      startLocation.longitude,
      endLocation.latitude,
      endLocation.longitude
    );

    let baseTime = (distance / 60) * 60; // Assume 60 km/h base speed

    // Traffic impact
    const trafficMultipliers = { light: 0.8, moderate: 1.0, heavy: 1.4 };
    const trafficImpact = trafficMultipliers[currentTraffic as keyof typeof trafficMultipliers] || 1.0;

    // Skill match bonus (reduces time for specialized responders)
    const skillMatchBonus = responderSkills.length > 2 ? 0.9 : 1.0;

    const estimatedTime = baseTime * trafficImpact * skillMatchBonus;

    // Generate simple route points (in real implementation, use routing API)
    const optimalRoute = [
      startLocation,
      {
        latitude: (startLocation.latitude + endLocation.latitude) / 2,
        longitude: (startLocation.longitude + endLocation.longitude) / 2
      },
      endLocation
    ];

    return {
      estimatedTime: Math.round(estimatedTime),
      trafficImpact,
      skillMatchBonus,
      optimalRoute
    };
  }

  private calculateDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
    const R = 6371; // Earth's radius in kilometers
    const dLat = ((lat2 - lat1) * Math.PI) / 180;
    const dLon = ((lon2 - lon1) * Math.PI) / 180;
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  async analyzeEmergencyPatterns(emergencies: any[]): Promise<{
    patterns: Array<{
      type: string;
      location: string;
      timePattern: string;
      frequency: number;
      prediction: string;
    }>;
    recommendations: string[];
  }> {
    // Simulate pattern analysis
    await new Promise(resolve => setTimeout(resolve, 800));

    const patterns = [
      {
        type: 'medical',
        location: 'Downtown area',
        timePattern: 'Peak hours (8-10 AM, 5-7 PM)',
        frequency: 0.85,
        prediction: 'High probability during rush hours'
      },
      {
        type: 'fire',
        location: 'Industrial district',
        timePattern: 'Weekday afternoons',
        frequency: 0.65,
        prediction: 'Increased risk during dry seasons'
      }
    ];

    const recommendations = [
      'Position additional medical units downtown during peak hours',
      'Increase fire department patrols in industrial areas',
      'Pre-position resources based on weather conditions',
      'Implement predictive deployment strategies'
    ];

    return { patterns, recommendations };
  }
}